import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  Billing2023PlanCodes,
  isBilling2023Subscription,
} from '@mm/state/model/SubscriptionModel';
import { useLocation, useParams } from 'react-router-dom';
import { enhancedPlansSelector } from '@mm/state/selectors/subscriptionSelectors';
import { useSelector } from 'react-redux';
import {
  activeFacebookPageSelector,
  getPageWithBillingInfo,
} from '@mm/state/selectors/facebookPageSelector';
import { StoreState } from '@mm/state/store-interfaces';
import { SettingsRoutes } from '../../../../../routes/settings-routes';
import { getBasePath } from '../SubscriptionTabsArea/ActiveSubscriptionTab/helpers';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { notification } from 'antd';
import { selectActiveCompanyId } from '@mm/state/selectors/viewSelectors';
import React, { ReactNode, useEffect, useState } from 'react';
import imageSrc from 'assets/cancel-subscription.png';
import { PlanSubtypes } from '@mm/state/interfaces/subscription';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import { useFreeTrialUpgradeModal } from '../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';

export type BillingPlanCardProps = {
  title: string;
  header?: null | string;
  description?: null | string;
  price: string | ReactNode;
  pricePeriod?: string;
  priceDiscount?: number;
  buttonName: string;
  planCodes?: string[];
  onClick?: () => void;
  threshold?: [number, number];
  points?: string[];
  secondaryPoints?: string[];
  hide?: boolean;
  isFreeTrial?: boolean;
};

export type ManageSubsctiptionRouteParams = {
  tabId: string;
  pageId: string;
};

export const FREE_PAID_PLAN_CODES = [
  Billing2023PlanCodes[0],
  Billing2023PlanCodes[1],
];
export const PRO_PLAN_CODES = [
  Billing2023PlanCodes[2],
  Billing2023PlanCodes[3],
];
export const STARTER_PLAN_CODES = [
  Billing2023PlanCodes[4],
  Billing2023PlanCodes[5],
];

export const useBillingPlans = ({
  isAnnual,
  onBillingSuccess,
  hideFreePlan,
  landmineOpenId,
  onBackToParent,
}) => {
  const plans = useSelector(enhancedPlansSelector);
  const basicFacebookPages = useSelector(
    (state: StoreState) => state.connectedPage.basicPagesList,
  );
  const plansMapping = useSelector(
    (state: StoreState) => state.subscription.plansMapping,
  );
  const companyId = useSelector(selectActiveCompanyId);
  const facebookPage = useSelector(activeFacebookPageSelector);
  const { tabId, pageId } = useParams<ManageSubsctiptionRouteParams>();
  const location = useLocation();
  const activeFacebookPage = pageId
    ? basicFacebookPages.find(p => p.id === Number(pageId))
    : facebookPage;

  const currentPlan = plans.find(plan =>
    tabId
      ? plan.planCode === tabId
      : activeFacebookPage?.subscriptions?.find(
          sub => sub.planCode === plan.planCode,
        ),
  );
  const isFreePlan = !activeFacebookPage?.subscriptions?.length;
  const iscurrentTeamCustomPlan = !!currentPlan?.planSubtypes.find(
    subtype => subtype === PlanSubtypes.teamCustom,
  );
  const isManageSubscriptionsPage = location.pathname.includes(
    getBasePath(SettingsRoutes.billing.manageSubscrptions.path),
  );

  const canCancelSubscription =
    !isFreePlan &&
    isManageSubscriptionsPage &&
    !(
      activeFacebookPage.subscription?.state === 'canceled' ||
      activeFacebookPage.downgradePending
    ) &&
    !activeFacebookPage.redemption?.freeTrial;

  const plansToDowngrade = plans.filter(plan =>
    currentPlan?.downgradePlansIds.includes(plan.id),
  );

  const { isFreeTrialPromotionActive } = useFreeTrialUpgradeModal();
  const isStarterPlan = currentPlan?.planSubtypes.includes(
    PlanSubtypes.starter2023,
  );
  const isProPlan = currentPlan?.planSubtypes.includes(PlanSubtypes.pro2023);
  const isFreePlusPlan = currentPlan?.planSubtypes.includes(
    PlanSubtypes.free2023,
  );
  const {
    closeModal,
    openContactSalesModal,
    openBillingModal,
    openNewInfoModal,
  } = useModalActions();

  const { retrieveBasicListByCompany } = useConnectedPageCRUDActions();
  const subscriptionCRUDActions = useSubscriptionCRUDActions();

  const [isAwaitingCancellation, setIsAwaitingCancellation] = useState(false);

  useEffect(() => {
    const getSubscriptionCancellationStatus = async () => {
      if (activeFacebookPage?.subscription?.id) {
        const hasCancels =
          await subscriptionCRUDActions.getCurrentSubscriptionCancels(
            activeFacebookPage.subscription.id,
          );

        if (hasCancels) {
          setIsAwaitingCancellation(true);
        }
      }
    };

    if (activeFacebookPage?.subscription?.id) {
      getSubscriptionCancellationStatus();
    }
  }, [activeFacebookPage?.subscription?.id]);

  const getPlanPriceByPeriod = (planCodes?: string[] | undefined) => {
    if (planCodes?.length) {
      const planCode = isAnnual ? planCodes[1] : planCodes[0];
      return getPlanPrice(planCode);
    }
  };

  const getPlanPrice = (planCode: string) => {
    const enhancedPlan = plans.find(p => p.planCode === planCode);
    return (enhancedPlan?.unitAmountInCents || 0) / 100;
  };

  const onBack = () => {
    if (onBackToParent) {
      onBackToParent();
      return;
    }
    closeModal();
  };

  const onTalkToSales = () => {
    openContactSalesModal({
      onBack,
    });
  };

  const getPlanDiscountPrice = (
    monthlyPlanCode?: string,
    annualPlanCode?: string,
  ) => {
    if (!monthlyPlanCode || !annualPlanCode) {
      return { discountedPrice: 0, discountPercent: 0 };
    }
    const monthlyPrice = getPlanPrice(monthlyPlanCode);
    const annualPrice = getPlanPrice(annualPlanCode);

    return {
      discountedPrice: monthlyPrice * 12 - annualPrice,
      discountPercent: 100 - (annualPrice * 100) / (monthlyPrice * 12),
    };
  };

  const getCurrentPlan = (planCodes?: string[]) => {
    const planCode = isAnnual ? planCodes?.[1] : planCodes?.[0];
    const plan = plans.find(p => p.planCode === planCode);
    return { planCode, plan };
  };

  const upgradeToPlan = async (planCodes: string[]) => {
    let enhancedBillingPage;

    if (!activeFacebookPage && !basicFacebookPages.length) {
      const res = await retrieveBasicListByCompany(companyId);
      enhancedBillingPage = getPageWithBillingInfo(
        res?.[0],
        plans,
        plansMapping,
      );
    } else {
      enhancedBillingPage = activeFacebookPage;
    }

    const subscriptionId = enhancedBillingPage?.subscription
      ? enhancedBillingPage.subscription.id
      : undefined;

    const { planCode, plan } = getCurrentPlan(planCodes);
    const downgrade = plansToDowngrade.find(plan => planCode === plan.planCode);

    if (
      !isBilling2023Subscription(enhancedBillingPage?.subscription) &&
      !isFreePlan
    ) {
      openNewInfoModal({
        title: 'Contact Support',
        description: (
          <div>
            Please contact support at{''}{' '}
            <a
              className="pause-subscription-modal-link"
              href="mailto:cs@customers.ai"
            >
              cs@customers.ai
            </a>
            <br />
            to upgrade your subscription
          </div>
        ),
        image: imageSrc,
        button: 'Ok',
        onClick: () => closeModal(),
      });
      return;
    }

    if (plan && enhancedBillingPage) {
      await openBillingModal({
        facebookPage: enhancedBillingPage,
        plan,
        landmineOpenId,
        manualRedirect: true,
        subscription: enhancedBillingPage?.subscription,
        upgradeFromSubscriptionId: !downgrade ? subscriptionId : undefined,
        downgradeFromSubscriptionId: downgrade ? subscriptionId : undefined,
        onSuccessBilling: onBillingSuccess,
        onPlanCreatedFailed: () =>
          notification.error({
            message: 'Something went wrong',
            description:
              'Subscription is not created. Credit card is not charged.',
          }),
        isMonthly: true,
        hideAnnualToggle: true, // annual plans are not implemented properly on BE, for now hide them
      });
    }
  };

  const billingPlans: BillingPlanCardProps[] = [
    {
      title: currentPlan?.name || '',
      header: '',
      price: `$${(currentPlan?.unitAmountInCents || 0) / 100}`,
      pricePeriod: `/${currentPlan?.billingPeriod}`,
      buttonName: 'Current Plan',
      planCodes: [currentPlan?.planCode] as string[],
      hide:
        !isManageSubscriptionsPage ||
        !currentPlan ||
        Billing2023PlanCodes.includes(currentPlan.planCode as any),
    },
    {
      title: 'Free',
      header: null,
      description:
        'No risk, no credit card required. Free lead capture and automated email outreach to website visitors.',
      price: '$0',
      pricePeriod: '',
      buttonName: 'Sign Up Free',
      onClick: () => {
        onBillingSuccess?.();
        closeModal();
      },
      threshold: [0, 250],
      points: ['50 Leads Included', '150 Email Sends Included'],
      hide: hideFreePlan,
    },
    {
      title: 'Free Trial',
      header: null,
      description:
        'No risk, no credit card required. Free lead capture and automated email outreach to website visitors.',
      price: (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            fontSize: '22px',
            fontWeight: 700,
          }}
        >
          7 days Free{' '}
          <span
            style={{ fontSize: '14px', fontWeight: 500, lineHeight: '22px' }}
          >
            up to 500 Leads
          </span>
        </div>
      ),
      pricePeriod: '',
      buttonName: 'Your current plan',
      onClick: () => {
        onBillingSuccess?.();
        closeModal();
      },
      hide: !isFreeTrialPromotionActive,
      isFreeTrial: true,
    },
    {
      title: 'Free Plus',
      price: `$${getPlanPriceByPeriod(FREE_PAID_PLAN_CODES)}`,
      buttonName: !isFreePlan ? 'Upgrade' : 'Get Started',
      planCodes: FREE_PAID_PLAN_CODES,
      onClick: () => upgradeToPlan(FREE_PAID_PLAN_CODES),
      threshold: [0, 250],
      points: [
        'Starting at $1 per lead',
        '5 cents per email send',
        ...(isFreePlusPlan
          ? [
              'Starting at $0.25 per Consumer Directory lead',
              'Starting at $0.01 per social send',
            ]
          : ''),
      ],
      hide: !hideFreePlan || iscurrentTeamCustomPlan,
    },
    {
      title: 'Starter',
      header: 'Best for businesses with up to 1,000 visitors per month.',
      description:
        'Automatically capture leads from website traffic and convert them with automated outreach sequences.',
      price: `$${getPlanPriceByPeriod(STARTER_PLAN_CODES)}`,
      pricePeriod: isAnnual ? '/year' : '/month',
      priceDiscount: getPlanDiscountPrice(
        STARTER_PLAN_CODES[0],
        STARTER_PLAN_CODES[1],
      ).discountedPrice,
      buttonName: !isFreePlan ? 'Upgrade' : 'Get Started',
      planCodes: STARTER_PLAN_CODES,
      onClick: () => upgradeToPlan(STARTER_PLAN_CODES),
      threshold: [250, 1000],
      points: [
        '250 X-Ray Leads Included',
        '750 Emails Included',
        '50 Consumer Directory Leads included',
        '1,000 Social Sends Included',
        'SMS/MMS. Pay as you go',
      ],
      secondaryPoints: [
        'Additional starting at $1 per Lead',
        'Additional starting at $0.04 per Email',
        ...(isStarterPlan
          ? [
              'Additional starting at $0.25 per Consumer Directory Lead',
              'Additional starting at $0.01 per Social Send',
              'Starting at $0.06 per SMS/MMS',
            ]
          : ''),
      ],
      hide: !isStarterPlan,
    },
    {
      title: 'Pro',
      header: 'Best for businesses with up to 4,000 visitors per month.',
      description:
        'Integrate sales outreach automation and website lead generation. Dedicated support and onboarding.',
      price: `$${getPlanPriceByPeriod(PRO_PLAN_CODES)}`,
      pricePeriod: isAnnual ? '/year' : '/month',
      priceDiscount: getPlanDiscountPrice(PRO_PLAN_CODES[0], PRO_PLAN_CODES[1])
        .discountedPrice,
      buttonName: !isFreePlan ? 'Upgrade' : 'Get Started',
      planCodes: PRO_PLAN_CODES,
      onClick: () => upgradeToPlan(PRO_PLAN_CODES),
      threshold: [1001, 4000],
      points: [
        '700 X-Ray Leads Included',
        '3,000 Emails Included',
        ...(isProPlan
          ? [
              '150 Consumer Directory Leads Included',
              '2,500 Social Sends Included',
              'SMS/MMS. Pay as you go',
            ]
          : ''),
      ],
      secondaryPoints: [
        'Additional starting at $0.8 per Lead',
        'Additional starting at $0.035 per Email',
        ...(isProPlan
          ? [
              'Additional starting at $0.2 per Consumer Directory Lead',
              'Additional starting at $0.01 per Social Send',
              'Starting at $0.06 per SMS/MMS',
            ]
          : ''),
      ],
      hide: !isProPlan,
    },

    {
      title: 'Custom',
      header: null,
      description:
        "Tailor-made plans designed to your business's specific needs from custom integrations to automation building and training.",
      price: (
        <span style={{ fontSize: '14px', fontWeight: 500, lineHeight: '22px' }}>
          Custom built solutions tailored to your business
        </span>
      ),
      pricePeriod: '',
      buttonName: 'Schedule a Demo',
      onClick: onTalkToSales,
      threshold: [4001, 25000],
      points: ['As low as $0.2 per lead', 'As low as .01 per email'],
      hide: false,
    },
  ];

  return {
    billingPlans,
    activeFacebookPage,
    currentPlan,
    tabId,
    pageId,
    plansToDowngrade,
    canCancelSubscription,
    isAwaitingCancellation,
    isManageSubscriptionsPage,
    getPlanDiscountPrice,
    getCurrentPlan,
  };
};
